import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/api";
import { ChevronLeft, ChevronRight, CalendarDays, DollarSign, Download, Lock, LockOpen } from "lucide-react";

const DOW = ["Wed", "Thu", "Fri", "Sat", "Sun", "Mon", "Tue"];

function shortDate(iso) {
  const [, m, d] = iso.split("-");
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
}
function rangeLabel(start, end) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [, sm, sd] = start.split("-");
  const [, em, ed] = end.split("-");
  return `${months[+sm - 1]} ${+sd} – ${months[+em - 1]} ${+ed}`;
}
function addDaysISO(iso, n) {
  const dt = new Date(iso + "T00:00:00");
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
}

export function WeeklyPayCard({ token }) {
  const [start, setStart] = useState(null); // server-normalised Monday
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [locking, setLocking] = useState(false);

  const load = useCallback(
    async (s) => {
      setLoading(true);
      try {
        const { data } = await adminApi(token).get("/admin/labor/week", {
          params: s ? { start: s } : {},
        });
        setData(data);
        setStart(data.week_start);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  const locked = !!data?.locked;

  const toggleLock = async () => {
    if (!start) return;
    if (locked && !window.confirm("Unlock this pay week so punches can be edited again?")) return;
    setLocking(true);
    try {
      const api = adminApi(token);
      if (locked) {
        await api.delete("/admin/labor/week/lock", { params: { start } });
      } else {
        await api.post("/admin/labor/week/lock", null, { params: { start } });
      }
      await load(start);
    } catch {
      /* ignore */
    } finally {
      setLocking(false);
    }
  };

  const exportCsv = async () => {
    if (!start) return;
    setExporting(true);
    try {
      const res = await adminApi(token).get("/admin/labor/week", {
        params: { start, format: "csv" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll-week-${start}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="weekly-pay-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-brand-red" />
          <h3 className="font-display text-brand-red text-xl leading-none">Weekly Pay</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => load(addDaysISO(start, -7))}
            disabled={!start || loading}
            data-testid="weekly-pay-prev"
            className="p-1.5 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-40"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            type="date"
            value={start || ""}
            onChange={(e) => e.target.value && load(e.target.value)}
            data-testid="weekly-pay-date"
            className="px-2 py-1.5 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
          />
          <button
            onClick={() => load(addDaysISO(start, 7))}
            disabled={!start || loading}
            data-testid="weekly-pay-next"
            className="p-1.5 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-40"
            aria-label="Next week"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => load(null)}
            disabled={loading}
            data-testid="weekly-pay-this-week"
            className="ml-1 px-2.5 py-1.5 rounded-lg border border-stone-300 text-[11px] uppercase tracking-widest font-bold hover:bg-stone-100 disabled:opacity-40"
          >
            This week
          </button>
          <button
            onClick={exportCsv}
            disabled={exporting || loading || !data || data.employees.length === 0}
            data-testid="weekly-pay-export"
            className="ml-1 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand-red text-white text-[11px] uppercase tracking-widest font-black hover:bg-red-800 disabled:opacity-40"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? "…" : "CSV"}
          </button>
          <button
            onClick={toggleLock}
            disabled={locking || loading || !start}
            data-testid="weekly-pay-lock"
            className={`ml-1 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] uppercase tracking-widest font-black disabled:opacity-40 ${
              locked
                ? "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200"
                : "bg-stone-900 text-white hover:bg-stone-700"
            }`}
          >
            {locked ? <LockOpen className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            {locking ? "…" : locked ? "Unlock" : "Lock"}
          </button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="text-sm text-stone-600">
            <span className="font-bold text-stone-800">{rangeLabel(data.week_start, data.week_end)}</span>
            <span className="text-stone-400 text-xs ml-1">(Wed–Tue · {data.timezone})</span>
            {locked && (
              <span
                data-testid="weekly-pay-locked-badge"
                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-[10px] uppercase tracking-widest font-black align-middle"
              >
                <Lock className="w-3 h-3" />
                Finalized
              </span>
            )}
            {locked && data.lock?.locked_at && (
              <div className="text-[11px] text-stone-400 mt-0.5">
                Finalized {data.lock.locked_at.slice(0, 10)} by {data.lock.locked_by || "admin"} — punches locked
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 bg-stone-900 text-brand-yellow rounded-xl px-4 py-2" data-testid="weekly-pay-total">
            <DollarSign className="w-4 h-4" />
            <div>
              <div className="text-[9px] uppercase tracking-widest opacity-80 leading-none">Total payroll</div>
              <div className="font-mono text-xl font-black leading-tight">{money(data.total_pay)}</div>
            </div>
            <div className="ml-3 border-l border-white/20 pl-3">
              <div className="text-[9px] uppercase tracking-widest opacity-80 leading-none">Hours</div>
              <div className="font-mono text-xl font-black leading-tight">{data.total_hours}</div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-stone-400 text-sm">Loading…</div>
      ) : !data || data.employees.length === 0 ? (
        <div className="py-8 text-center text-stone-400 text-sm" data-testid="weekly-pay-empty">
          No clocked hours this week.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm border-collapse min-w-[560px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                <th className="text-left font-black py-2 pr-2 sticky left-0 bg-white">Employee</th>
                {data.days.map((iso, i) => (
                  <th key={iso} className="text-center font-bold px-1 py-2 whitespace-nowrap">
                    <div>{DOW[i]}</div>
                    <div className="text-stone-400 font-mono">{shortDate(iso)}</div>
                  </th>
                ))}
                <th className="text-right font-black px-2 py-2">Hrs</th>
                <th className="text-right font-black px-2 py-2">OT</th>
                <th className="text-right font-black px-2 py-2">Rate</th>
                <th className="text-right font-black pl-2 py-2">Pay</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => (
                <tr key={e.employee_id || e.name} className="border-t border-stone-100" data-testid={`weekly-pay-row-${e.name}`}>
                  <td className="py-2 pr-2 font-bold text-stone-800 sticky left-0 bg-white whitespace-nowrap">{e.name}</td>
                  {data.days.map((iso) => {
                    const h = e.days[iso] || 0;
                    return (
                      <td key={iso} className={`text-center px-1 py-2 font-mono ${h ? "text-stone-700" : "text-stone-300"}`}>
                        {h ? h.toFixed(2) : "—"}
                      </td>
                    );
                  })}
                  <td className="text-right px-2 py-2 font-mono font-bold">{e.hours.toFixed(2)}</td>
                  <td className={`text-right px-2 py-2 font-mono ${e.ot_hours > 0 ? "text-amber-600 font-bold" : "text-stone-300"}`}>
                    {e.ot_hours > 0 ? e.ot_hours.toFixed(2) : "—"}
                  </td>
                  <td className="text-right px-2 py-2 font-mono text-stone-500">{money(e.pay_rate)}</td>
                  <td className="text-right pl-2 py-2 font-mono font-black text-brand-red">{money(e.pay)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-200 text-stone-900">
                <td className="py-2 pr-2 font-black sticky left-0 bg-white">Total</td>
                <td colSpan={7}></td>
                <td className="text-right px-2 py-2 font-mono font-black">{data.total_hours}</td>
                <td className="text-right px-2 py-2 font-mono font-black text-amber-600">
                  {data.total_ot_hours > 0 ? data.total_ot_hours : "—"}
                </td>
                <td></td>
                <td className="text-right pl-2 py-2 font-mono font-black text-brand-red">{money(data.total_pay)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
